// src/pages/finance-portal/ic-netting/IcNetting.js
//
// IC Settlement & Netting list

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, Tabs, Tab, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select,
    FormControl, InputLabel,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT:     { bg: '#f1f5f9', color: '#64748b' },
    COMPUTED:  { bg: '#fef9c3', color: '#854d0e' },
    APPROVED:  { bg: '#dbeafe', color: '#1d4ed8' },
    SETTLED:   { bg: '#dcfce7', color: '#166534' },
    CANCELLED: { bg: '#fecaca', color: '#991b1b' },
};

const STATUS_TABS = ['ALL', 'DRAFT', 'COMPUTED', 'APPROVED', 'SETTLED'];

const FREQ_OPTS = [
    { value: 'MONTHLY', label: 'Monthly' },
    { value: 'QUARTERLY', label: 'Quarterly' },
    { value: 'AD_HOC', label: 'Ad-hoc' },
];

function fmt(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 0,
    }).format(val || 0);
}

export default function IcNetting() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);

    const [createDialog, setCreateDialog] = useState({
        open: false, name: '', description: '', period_start: '', period_end: '',
        frequency: 'MONTHLY', base_currency: 'USD', notes: '',
    });

    const fetchRuns = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const status = STATUS_TABS[statusTab];
            if (status !== 'ALL') params.status = status;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/ic-netting/runs', { params });
            if (res.data.success) {
                setRows(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, statusTab, search]);

    useEffect(() => { fetchRuns(); }, [fetchRuns]);

    const submitCreate = async () => {
        try {
            const res = await axiosInstance.post('/api/finance/ic-netting/runs', {
                name: createDialog.name?.trim(),
                description: createDialog.description?.trim() || null,
                period_start: createDialog.period_start,
                period_end: createDialog.period_end,
                frequency: createDialog.frequency,
                base_currency: (createDialog.base_currency || 'USD').toUpperCase(),
                notes: createDialog.notes || null,
            });
            enqueueSnackbar('Netting run created', { variant: 'success' });
            setCreateDialog({ ...createDialog, open: false });
            const id = res.data?.data?.id;
            if (id) navigate(`/finance-portal/ic-netting/${id}`);
            else fetchRuns();
        } catch (err) {
            const errs = err.response?.data?.errors;
            if (errs) {
                enqueueSnackbar('Validation failed: ' + Object.values(errs).join(', '), { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
            }
        }
    };

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                        textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                        letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                            <CompareArrowsIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white',
                                                          fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                IC Settlement & Netting
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)',
                                              fontSize: '0.9rem', mt: 0.25 }}>
                                Multilateral netting of intercompany balances per period
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />}
                            onClick={() => setCreateDialog({
                                ...createDialog, open: true, name: '', description: '',
                                period_start: '', period_end: '', frequency: 'MONTHLY',
                                base_currency: 'USD', notes: '',
                            })}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                  bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                        New Run
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)}
                                  sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="Search by run number / name..."
                               value={search}
                               onChange={e => { setSearch(e.target.value); setPage(0); }}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start">
                                       <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                   </InputAdornment>,
                                   sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                               }}
                               sx={{ minWidth: { xs: '100%', sm: 360 } }} />
                </Box>

                <Tabs value={statusTab} onChange={(_, v) => { setStatusTab(v); setPage(0); }}
                      sx={{ mb: 2,
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none',
                                                fontSize: '0.85rem', color: '#64748b', minHeight: 40, py: 1 },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' } }}>
                    {STATUS_TABS.map(t => <Tab key={t} label={t} />)}
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Run #', 'Name', 'Period', 'Frequency', 'Pairs',
                                      'Gross', 'Net', 'Reduction %', 'Status'].map(h =>
                                        <TableCell key={h} sx={labelCell}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [1,2,3].map(i => (
                                        <TableRow key={i}><TableCell colSpan={9}><Skeleton height={36} /></TableCell></TableRow>
                                    ))
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                                            <CompareArrowsIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>
                                                No netting runs yet
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(r => {
                                    const sStyle = STATUS_COLORS[r.status] || STATUS_COLORS.DRAFT;
                                    return (
                                        <TableRow key={r.id} hover
                                                  onClick={() => navigate(`/finance-portal/ic-netting/${r.id}`)}
                                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {r.run_number}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, color: '#1e293b', fontWeight: 500 }}>
                                                {r.name}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {r.period_start} → {r.period_end}
                                            </TableCell>
                                            <TableCell sx={dataCell}>{r.frequency}</TableCell>
                                            <TableCell sx={dataCell}>{r.line_count}</TableCell>
                                            <TableCell sx={dataCell}>
                                                {fmt(r.total_gross, r.base_currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {fmt(r.total_net, r.base_currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, color: '#16a34a', fontWeight: 600 }}>
                                                {r.savings_pct != null
                                                    ? `${(r.savings_pct * 100).toFixed(1)}%`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={r.status} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: sStyle.bg, color: sStyle.color }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component="div" count={total} page={page}
                                     onPageChange={(_, p) => setPage(p)}
                                     rowsPerPage={rowsPerPage}
                                     onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                     sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }} />
                </Card>
            </Box>

            <Dialog open={createDialog.open}
                    onClose={() => setCreateDialog({ ...createDialog, open: false })}
                    PaperProps={{ sx: { borderRadius: '16px' } }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>New Netting Run</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                        <TextField label="Name *" size="small"
                                   value={createDialog.name}
                                   onChange={e => setCreateDialog(p => ({ ...p, name: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <TextField label="Description" size="small" multiline rows={2}
                                   value={createDialog.description}
                                   onChange={e => setCreateDialog(p => ({ ...p, description: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField label="Period Start *" type="date" size="small" fullWidth
                                       value={createDialog.period_start}
                                       onChange={e => setCreateDialog(p => ({ ...p, period_start: e.target.value }))}
                                       InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                            <TextField label="Period End *" type="date" size="small" fullWidth
                                       value={createDialog.period_end}
                                       onChange={e => setCreateDialog(p => ({ ...p, period_end: e.target.value }))}
                                       InputLabelProps={{ shrink: true, sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <FormControl size="small" fullWidth>
                                <InputLabel sx={{ fontFamily, fontSize: '0.85rem' }}>Frequency</InputLabel>
                                <Select value={createDialog.frequency} label="Frequency"
                                        onChange={e => setCreateDialog(p => ({ ...p, frequency: e.target.value }))}
                                        sx={{ fontFamily, borderRadius: '10px' }}>
                                    {FREQ_OPTS.map(o =>
                                        <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField label="Base Currency" size="small" sx={{ width: 120 }}
                                       value={createDialog.base_currency}
                                       onChange={e => setCreateDialog(p => ({ ...p, base_currency: e.target.value.toUpperCase() }))}
                                       inputProps={{ maxLength: 3 }}
                                       InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                       InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                        </Box>
                        <TextField label="Notes" size="small" multiline rows={2}
                                   value={createDialog.notes}
                                   onChange={e => setCreateDialog(p => ({ ...p, notes: e.target.value }))}
                                   InputProps={{ sx: { fontFamily, fontSize: '0.85rem' } }}
                                   InputLabelProps={{ sx: { fontFamily, fontSize: '0.85rem' } }} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateDialog({ ...createDialog, open: false })}
                            sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" onClick={submitCreate}
                            sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT,
                                  '&:hover': { bgcolor: '#16a34a' } }}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
